import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { Loader2 } from "lucide-react";

import { PublicLayout } from "@/components/layout/PublicLayout";
import Home from "@/pages/Home";
import SearchResults from "@/pages/SearchResults";
import TaxiDetail from "@/pages/TaxiDetail";
import BookingFlow from "@/pages/BookingFlow";
import Checkout from "@/pages/Checkout";
import PaymentSuccess from "@/pages/PaymentSuccess";
import PaymentCancel from "@/pages/PaymentCancel";
import Confirmation from "@/pages/Confirmation";
import TrackRide from "@/pages/TrackRide";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import Airport from "@/pages/Airport";
import Station from "@/pages/Station";
import Outstation from "@/pages/Outstation";
import Tourist from "@/pages/Tourist";
import Corporate from "@/pages/Corporate";
import BecomeDriver from "@/pages/BecomeDriver";
import Help from "@/pages/Help";
import Messages from "@/pages/Messages";
import CustomerDashboard from "@/pages/customer/Dashboard";
import DriverDashboard from "@/pages/driver/Dashboard";
import AdminDashboard from "@/pages/admin/Dashboard";
import AdminLogin from "@/pages/admin/AdminLogin";
import AgentDashboard from "@/pages/agent/Dashboard";

function Protected({ children, roles }) {
  const { user, ready } = useAuth();
  if (!ready) return <div className="flex h-screen items-center justify-center"><Loader2 className="animate-spin text-brand" size={32} /></div>;
  if (!user) return <Navigate to="/login" replace />;
  if (roles && !roles.includes(user.role)) return <Navigate to="/" replace />;
  return children;
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            <Route element={<PublicLayout />}>
              <Route path="/" element={<Home />} />
              <Route path="/search" element={<SearchResults />} />
              <Route path="/taxi/:id" element={<TaxiDetail />} />
              <Route path="/book" element={<BookingFlow />} />
              <Route path="/checkout/:bookingId" element={<Checkout />} />
              <Route path="/payment/success" element={<PaymentSuccess />} />
              <Route path="/payment/cancel" element={<PaymentCancel />} />
              <Route path="/confirmation/:ref" element={<Confirmation />} />
              <Route path="/track/:id" element={<Protected><TrackRide /></Protected>} />
              <Route path="/airport-transfers" element={<Airport />} />
              <Route path="/station-transfers" element={<Station />} />
              <Route path="/outstation" element={<Outstation />} />
              <Route path="/tourist" element={<Tourist />} />
              <Route path="/corporate" element={<Corporate />} />
              <Route path="/become-a-driver" element={<BecomeDriver />} />
              <Route path="/help" element={<Help />} />
              <Route path="/messages" element={<Protected><Messages /></Protected>} />
              <Route path="/dashboard" element={<Protected roles={["customer"]}><CustomerDashboard /></Protected>} />
            </Route>
            <Route path="/login" element={<Login />} />
            <Route path="/admin/login" element={<AdminLogin />} />
            <Route path="/register" element={<Register />} />
            <Route path="/driver" element={<Protected roles={["driver", "operator"]}><DriverDashboard /></Protected>} />
            <Route path="/admin" element={<Protected roles={["admin", "super_admin"]}><AdminDashboard /></Protected>} />
            <Route path="/agent" element={<Protected roles={["agent", "admin", "super_admin", "support"]}><AgentDashboard /></Protected>} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          <Toaster position="top-right" richColors />
        </BrowserRouter>
      </AuthProvider>
    </div>
  );
}

export default App;
